import { Component, OnInit, ViewContainerRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalService } from 'src/app/services/modal/modal.service';
import { TableRadioButtonLabelValue } from 'src/app/utils/interfaces/new/table-radio-button-interface';
import { Droits } from 'src/app/utils/interfaces/new/droits.interface';
import { Role } from 'src/app/utils/interfaces/new/role.interface';
import { CreateRole } from 'src/app/utils/interfaces/new/create-role.interface';
import { Droit } from 'src/app/utils/interfaces/new/droit.interface';
import { DroitsService } from 'src/app/services/droits/droits.service';
import { RoleService } from 'src/app/services/role/role.service';
import { Paginated } from 'src/app/utils/interfaces/new/paginated.interface';

@Component({
  selector: 'app-create-role',
  templateUrl: './create-role.component.html',
  styleUrls: ['./create-role.component.scss']
})
export class CreateRoleComponent implements OnInit {

  public role: Role = null;
  private initialRole: Role = null;
  public droits: Paginated<Droit>;
  public formGroup: FormGroup;
  public keysHeaderLabel = '';
  public keysHeaderValue: Array<TableRadioButtonLabelValue> = [];
  public domainsList: Array<TableRadioButtonLabelValue> = [];
  public inputProfileNameErrorMsg = 'Le nom du rôle doit être renseigné';
  public roleId: string;
  public newObj: Droits[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private modalCtrl: ModalService,
    private viewContainerRef: ViewContainerRef,
    private roleSvc: RoleService,
    private droitSvc: DroitsService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit(): void {
    const loader = this.modalCtrl.openLoading(this.viewContainerRef);
    this.initData();
    this.modalCtrl.close(loader);
  }

  private async initData(): Promise<void> {
    const loader = this.modalCtrl.openLoading(this.viewContainerRef);
    this.roleId = this.route.snapshot.paramMap.get('id');
    this.droits = await this.droitSvc.find();
    if (this.roleId && this.roleId !== '') {
      this.role = await this.getRole(this.roleId);
    }

    this.initialRole = this.role ? this.role : null;

    this.formGroup = this.formBuilder.group({
      name: [this.role && this.role.nom && this.role.nom !== '' ? this.role.nom : '',
      [Validators.required, Validators.minLength(1)]
      ],
      espacePerso: [this.role && this.role.espacePerso ? this.role.espacePerso : false],
      donneesMedicales: [this.role && this.role.donneesMedicales ? this.role.donneesMedicales : false]
    });

    this.keysHeaderLabel = 'Domaines';
    this.keysHeaderValue = [
      { id: '', label: 'Contrôle total', value: '3' },
      { id: '', label: 'Ecriture', value: '2' },
      { id: '', label: 'Lecture', value: '1' },
      { id: '', label: 'Aucun', value: '0' },
    ];

    if (this.role?.droits) {
      for (const droit of this.role.droits) {
        this.domainsList.push({
          id: droit.id,
          label: droit.nom,
          value: droit.acces.toString(),
        });
      }
    } else {
      for (const droit of this.droits.objects) {
        this.domainsList.push({
          id: droit.id,
          label: droit.nom,
          value: '0'
        });
      }
    }
    this.modalCtrl.close(loader);
  }

  private async isRoleNameValid(): Promise<boolean | string> {
    // On vérifie que l'input est valide
    if (!this.formGroup.valid) {
      return 'Le nom du rôle doit être renseigné';
    }

    // On vérifie que le nom du role renseigné est différent du nom de role initial et qu'il n'existe pas déjà en base de donnée
    if (((this.initialRole && this.initialRole.nom !== this.formGroup.get('name').value)
      && await this.roleSvc.isRoleExists(this.formGroup.get('name').value))
      || (!this.initialRole && await this.roleSvc.isRoleExists(this.formGroup.get('name').value))) {
      return 'Ce rôle existe déjà';
    }

    return true;
  }

  private async getRole(roleId: string): Promise<Role> {
    return new Promise(async (resolve, reject) => {
      try {
        const roleTmp = await this.roleSvc.findOne(roleId);
        resolve(roleTmp);
      } catch (e) {
        reject(e);
      }
    });
  }

  public async doSaveRole(): Promise<void> {
    // Ouverture du loader
    const loader = this.modalCtrl.openLoading(this.viewContainerRef);
    try {
      // On vérifie la validité du nom du role
      const isValid = await this.isRoleNameValid();
      if (isValid !== true && typeof isValid === 'string') {
        this.modalCtrl.openAlert(this.viewContainerRef, 'Erreur', isValid as string);
        return;
      }

      for (const right of this.domainsList) {
        const droitsObj: Droits = {
          id: right.id,
          nom: right.label,
          acces: Number(right.value),
        };
        this.newObj.push(droitsObj);
      }


      const role: Role = {
        id: this.roleId,
        nom: this.formGroup.get('name').value,
        espacePerso: this.formGroup.get('espacePerso').value,
        donneesMedicales: this.formGroup.get('donneesMedicales').value,
        disabled: false,
        droits: this.newObj
      };

      const newRole: CreateRole = {
        nom: this.formGroup.get('name').value,
        disabled: false,
        espacePerso: this.formGroup.get('espacePerso').value,
        droits: this.newObj,
        donneesMedicales: this.formGroup.get('donneesMedicales').value,
      };

      // Si un initialRole existe, alors on enregistre les modifications
      if (this.initialRole) {
        await this.roleSvc.update(role);
      } else {
        // Sinon on créé le role
        await this.roleSvc.create(newRole);
      }

      // On ouvre une modale pour afficher un message indiquant que l'enregistrement s'est bien passé
      let confirmMsg = 'Rôle créé avec succès';
      if (this.initialRole) {
        confirmMsg = 'Modifications enregistrées avec succès';
      }
      const modal = this.modalCtrl.openAlert(this.viewContainerRef, 'Succès', confirmMsg);

      const modalSub = modal.instance.closeEmitter.subscribe(async () => {
        modalSub.unsubscribe();

        // On navigue vers la page d'administration
        await this.quitPage();
      });
    } catch (e) {
      throw e;
    } finally {
      // Fermeture du loader
      this.modalCtrl.close(loader);
    }
  }

  public async reactive(role: Role): Promise<void> {
    const modal = this.modalCtrl.openModalConfirmation(this.viewContainerRef, 'Confirmation', 'Voulez-vous vraiment réactiver ce rôle ?');
    modal.instance.closeEmitter.subscribe(async (res: boolean) => {
      if (res) {
        const loadingModal = this.modalCtrl.openLoading(this.viewContainerRef);
        try {
          await this.roleSvc.delete(role);
          //await this.initData();
          this.modalCtrl.close(loadingModal);
          this.modalCtrl.openAlert(this.viewContainerRef, 'Succès', 'Le rôle a été réactivé avec succès.');
        } catch (e) {
          this.modalCtrl.close(loadingModal);
          console.error(e);
        }
      }
    });
  }

  public async quitPage(): Promise<void> {
    try {
      this.router.navigate(['administration/role-management']);
    } catch (e) {
      throw e;
    }
  }

}
